import React, { useMemo, useEffect, useState } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Heading,
  useColorModeValue,
  Stack,
} from '@chakra-ui/react';
import { useToast, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalOverlay } from '@mybridge/ui';
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import API from 'v4/store/common/api';
// import { getCalendarIntegrationURL } from 'v4/lib/api/zoom-integration'; // Update with the correct path
import { useRouter } from 'next/router';
import { getZoomIntegrationCallback, getZoomIntegrationUrl } from 'v4/store/slices/settingsSlice'; // Update with the correct path
import { ZoomOragnizationIcon } from '@mybridge/icons/ZoomOragnizationIcon';
import { ZoomPersonalIcon } from '@mybridge/icons/ZoomPersonalIcon';
import { ZoomIcon } from '@mybridge/icons/ZoomIcon';
import { ZoomMeetSuggestion } from '@mybridge/icons/ZoomMeetSuggestion';
import { getZoomMeetIntegrationUrl } from 'v4/lib/api/zoom-integration';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';

const ZoomConferencingCard = ({ calendars, setCalendars }) => {
  const { t } = useTranslator();
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const [selectedCalendars, setSelectedCalendars] = useState([]);
  const { confrenceList } = useSelector((state) => state.settings);
  const dispatch = useDispatch();
  const router = useRouter();
  const toast = useToast();
  const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] = useState({
    isOpen: false,
  });

  // Find Zoom Meet calendar from the list
  const zoomMeetCalendar = useMemo(
    () => calendars?.find((calendar) => calendar?.key === "zoom"),
    [calendars]
  );
  const handleRemoveCalendar = async (selectedAccount) => {
    try {
      const response = await API({
        url: `api/v1/conference/${selectedAccount?.id}`,
        method: 'DELETE',
      });

      if (response?.message === 'appointment deleted successfully') {
        toast({
          title: t('settingsIntegration.zoomRemoved') || 'zoom removed successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setCalendars((prevCalendars) =>
          prevCalendars.map((calendar) =>
            calendar.id === selectedAccount?.id
              ? { ...calendar, isConnected: false }
              : calendar
          )
        );
      } else {
        throw new Error('Failed to remove');
      }
    } catch (error) {
      toast({
        title: t('settingsIntegration.errorRemoving') || 'Error removing calendar.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsConfirmRemoveModalOpen({ isOpen: false });
    }
  };

  useEffect(() => {
    if (confrenceList?.length > 0) {
      // Log the initial state of confrenceList and calendars to verify data structure


  
      const updatedSelected = confrenceList
        .map((cal) => {
          // Check the provider for each conference item

  
          // Find the Zoom calendar from the calendars list
          const calendar = calendars?.find((calendar) => {
            // Ensure we are matching only Zoom calendars
            const isZoomProvider = cal?.provider?.toLowerCase() === "zoom";
            const isZoomKey = calendar?.key?.toLowerCase().includes("zoom");
  
            return isZoomProvider && isZoomKey; // Both must be true
          });
          
          // Log matched calendar to verify correct mapping

  
          if (calendar) {
            return { ...calendar, email: cal?.calendar_id, id: cal?.id };
          }
          return null;
        })
        .filter(Boolean); // Filter out any null values
  
      // Log the final selected calendars for verification

  
      // Update the state with the selected calendars
      setSelectedCalendars(updatedSelected);
    }
  }, [confrenceList, calendars]);
  
  

  // Handle Zoom integration callback
  useEffect(() => {
    if (typeof window !== 'undefined' && router?.query?.code) { // Ensure code runs only on client-side
      const payload = {
        state: router?.query?.state,
        scope: router?.query?.scope,
        code: router?.query?.code,
        prompt: router?.query?.prompt,
        authuser: router?.query?.authuser,
        hd: router?.query?.hd,
      };
      dispatch(getZoomIntegrationCallback(payload)).then((res) => { // Update with correct action
        toast({
          title: 'Success',
          description: res?.payload?.data,
          status: 'success',
          duration: 9000,
          isClosable: true,
        });
        router.push('/settings/integration?status=success');
      });
    }
  }, [router, dispatch, toast]);

  // Mutation for connecting Zoom account
  const { mutate, isLoading, error } = useMutation(getZoomMeetIntegrationUrl, {
    onSuccess: (data) => {
      if (data?.error) {
        alert(data?.error);
      } else {
        const { url = null } = data;
        if (url) {
          router?.push(url); // Use router push to redirect to the URL
        }
      }
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const conferenceOptions = [
    {
      id: 'organization',
      title: t('settingsIntegration.setupOrganization') || 'Set up Zoom for your organization',
      icon: <ZoomOragnizationIcon />,
      requirements: [
        t('settingsIntegration.orgRequirement1') || 'As a myBridge administrator, be certain that you are your organization’s Zoom account administrator',
        t('settingsIntegration.orgRequirement2') || 'A Zoom account',
        t('settingsIntegration.orgRequirement3') || 'Users who are in the same Zoom account',
      ],
      description: [
        t('settingsIntegration.orgDescription1') || 'Set up on behalf of all of your users',
        t('settingsIntegration.orgDescription2') || 'Set as the default location for meetings',
        t('settingsIntegration.orgDescription3') || 'Control usage within Calendly',
      ],
      link: '',
      buttonLabel: t('settingsIntegration.connectOrganization') || 'Connect Zoom for my organization',
      opacity:0.5
    },
    {
      id: 'personal',
      title: t('settingsIntegration.setupPersonal') || 'Set up Zoom for yourself',
      icon: <ZoomPersonalIcon />,
      requirements: [
        t('settingsIntegration.personalRequirement1') || 'A Zoom account',
        t('settingsIntegration.personalRequirement2') || 'Your Zoom account administrator must',
      ],
      link: t('settingsIntegration.preApprove') || 'pre-approve myBridge in the Zoom Marketplace',
      description: [
        t('settingsIntegration.personalDescription1') || 'Automatically create Zoom meetings at the time an event is scheduled',
        t('settingsIntegration.personalDescription2') || 'Instantly share unique conferencing details upon confirmation',
      ],
      buttonLabel: t('settingsIntegration.connectPersonal') || 'Connect Zoom for myself',
      opacity:1

    },
  ];

  const ZoomMeetConnected = () => {
    return (
      <VStack
        spacing={6}
        align="start"
        p={6}
        mt={6}
        w="100%"
        maxW="100%"
        borderRadius="4px"
        boxShadow="0px 0px 12px 0px #00000033"
        backgroundColor={cardBg}
        fontFamily="Inter"
      >
        <VStack
          w="100%"
          p={4}
          alignItems="start"
          borderRadius="4px"
          border="1px solid #D9D9D9"
          backgroundColor="#FFFFFF"
          spacing={4}
        >
          <Text
            fontSize="18px"
            fontWeight="400"
            lineHeight="25.2px"
            color="#747474"
          >
            {t('settingsIntegration.zoomConnected') || "Congratulations! You can already schedule with Zoom video conferencing because your Zoom account is connected with myBridge."}
          </Text>
          <HStack w="100%" alignItems="center">
            <ZoomIcon width="80" height="80" />
            <VStack w="80%" gap={0} h={46} alignItems="flex-start">
              <Text fontSize="12px" fontWeight="400" color="#37ABE7">
                {t('settingsIntegration.connectedBy') || "CONNECTED BY"}
              </Text>
              <Text fontSize="22px" fontWeight="500" color="#5B5B5B">
                {selectedCalendars?.[0]?.email || t('settingsIntegration.noEmailFound') || 'No email found'}
              </Text>
            </VStack>
            <Button
              borderRadius="5px"
              fontWeight={600}
              fontSize="14px"
              color="#3D5A80"
              variant="outline"
              size="sm"
              onClick={() => setIsConfirmRemoveModalOpen({ isOpen: true })}
            >
              {t('settingsIntegration.disconnect') || "Disconnect"}
            </Button>
          </HStack>
        </VStack>

        <VStack
          w="100%"
          p={4}
          alignItems="start"
          borderRadius="4px"
          border="1px solid #D9D9D9"
          backgroundColor="#FFFFFF"
          spacing={4}
        >
          <Text fontSize="18px" fontWeight="500" color="#5B5B5B">
            {t('settingsIntegration.zoomFeatures') || "With your connected Zoom account you can:"}
          </Text>
          <HStack w="100%" alignItems="start" justifyContent="space-between">
            <ZoomMeetSuggestion />

            <Box w="48%">
              <Text fontSize="14px" fontWeight="500" color="#5B5B5B">
                {t('settingsIntegration.setDefaultLocation') || "Set a default location for event types."}
              </Text>
              <Text fontSize="12px" lineHeight="18px" color="#747474" mt={2}>
                {t('settingsIntegration.zoomDefault') || "Select Zoom video conferencing as a default location for any of your event types to have meeting details automatically added to calendar events."}
              </Text>
            </Box>
          </HStack>
        </VStack>
      </VStack>
    );
  };

  const renderConferenceCard = (option) => (
    <Box
      key={option.id}
      maxW="47%"
      w="100%"
      p="16px 30px"
      bg={cardBg}
      borderRadius="20px"
      border={`1px solid ${cardBorder}`}
      boxShadow="0px 0px 12px 0px #00000033"
      opacity={option?.opacity}
    >
      <VStack alignItems="center">
        <Heading
          fontSize="18px"
          fontWeight={400}
          color="#5B5B5B"
          lineHeight="21.6px"
        >
          {option.title}
        </Heading>
        <Box my={8}>{option.icon}</Box>
      </VStack>
      <VStack spacing={4} alignItems="flex-start">
        <Text ml={5}>
          <ul>
            {option.description.map((desc, idx) => (
              <li
                key={idx}
                style={{
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  color: '#747474',
                }}
              >
                {desc}
              </li>
            ))}
          </ul>
        </Text>
        <Text
          fontWeight="500"
          ml={-2}
          mb={-3}
          fontSize="14px"
          color="#5B5B5B"
        >
          {t('settingsIntegration.requirements') || "Requirements"}
        </Text>
        <VStack alignItems="start">
          <Text ml={5}>
            <ul>
              {option.requirements.map((req, idx) => (
                <li
                  key={idx}
                  style={{
                    fontSize: '12px',
                    fontWeight: 400,
                    lineHeight: '20px',
                    color: '#747474',
                  }}
                >
                  {req}
                </li>
              ))}
            </ul>
          </Text>
          <Text fontSize="12px" color="#37ABE7" mt={-3}>
            {option?.link}
          </Text>
        </VStack>
        <Button
          w="full"
          borderRadius="30px"
          _hover={{ bg: '#3D5A80' }}
          fontWeight={600}
          fontSize={15}
          lineHeight={25}
          padding="10px 30px"
          color="#FFFFFF"
          bg="#3D5A80"
          onClick={() => {
            
          option?.opacity===1?  mutate():console.log("organization")
          
          
          }} // Use mutation for integration
        >
          {option.buttonLabel}
        </Button>
      </VStack>
    </Box>
  );

  return (
    <>
      {zoomMeetCalendar?.isConnected ? (
        <ZoomMeetConnected />
      ) : (
        <Box
          display="flex"
          justifyContent="flex-start"
          fontFamily="Inter"
          gap={6}
          pt={4}
          flexWrap="wrap"
        >
          {conferenceOptions?.map((option) => renderConferenceCard(option))}
        </Box>
      )}

      <Modal
        isOpen={isConfirmRemoveModalOpen?.isOpen}
        onClose={() => setIsConfirmRemoveModalOpen({ isOpen: false })}
        isCentered
      >
        <ModalOverlay />
        <ModalContent w={350} h={107}>
          <ModalCloseButton />
          <ModalBody>
            <Stack mb={2}>
              <Text fontSize={16} color="#5B5B5B">
                {t('settingsIntegration.disconnect') || "Disconnect"} Zoom?
              </Text>
            </Stack>
            <ModalFooter pr={0} pt={4}>
              <Button
                fontSize={16}
                borderRadius={'full'}
                variant="ghost"
                onClick={() => setIsConfirmRemoveModalOpen({ isOpen: false })}
              >
                {t('myCalendar.dismiss') || "Dismiss"}
              </Button>
              <Button
                variant={'primary'}
                fontSize={15}
                borderRadius={'full'}
                onClick={() => handleRemoveCalendar(selectedCalendars?.[0])}
                // backgroundColor="#E4E6EB"
              >
                {t('myCalendar.confirm') || "Confirm"}
              </Button>
            </ModalFooter>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ZoomConferencingCard;
